import { FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../hooks/useAuth";
import { Input } from "../components/ui/input";
import { PasswordInput } from "../components/ui/PasswordInput";
import { PhoneInput } from "../components/ui/PhoneInput";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { useToast } from "../components/ui/use-toast";
import { SocialAuthButtons } from "../components/SocialAuthButtons";

const RegisterPage = () => {
  const { t } = useTranslation();
  const { register, isLoading } = useAuth();
  const { toast } = useToast();
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
  });
  const navigate = useNavigate();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [event.target.name]: event.target.value }));
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    try {
      await register(form);
      toast({
        title: t("auth.register.successTitle"),
        description: t("auth.register.successDescription"),
        variant: "success",
      });
      navigate("/login", { replace: true });
    } catch (err) {
      const message =
        typeof err === "string"
          ? err
          : err instanceof Error
          ? err.message
          : t("auth.register.errorDescription");
      toast({
        title: t("auth.register.errorTitle"),
        description: message,
        variant: "destructive",
      });
    }
  };

  return (
    <section className="card form-card space-y-6">
      <SocialAuthButtons onSuccess={() => navigate("/dashboard", { replace: true })} />
      <div className="text-center text-sm text-muted-foreground dark:text-white/60">{t("auth.register.subtitle")}</div>
      <form onSubmit={handleSubmit} className="stack-column">
        <div className="stack-column">
          <Label htmlFor="firstName">{t("auth.register.firstNameLabel")}</Label>
          <Input
            id="firstName"
            name="firstName"
            value={form.firstName}
            onChange={handleChange}
            required
          />
        </div>
        <div className="stack-column">
          <Label htmlFor="lastName">{t("auth.register.lastNameLabel")}</Label>
          <Input
            id="lastName"
            name="lastName"
            value={form.lastName}
            onChange={handleChange}
            required
          />
        </div>
        <div className="stack-column">
          <Label htmlFor="email">{t("auth.register.emailLabel")}</Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={form.email}
            onChange={handleChange}
            required
          />
        </div>
        <div className="stack-column">
          <Label htmlFor="phone">{t("auth.register.phoneLabel")}</Label>
          <PhoneInput
            id="phone"
            value={form.phone}
            onChange={(value) => setForm((prev) => ({ ...prev, phone: value }))}
            placeholder={t("auth.register.phonePlaceholder")}
            required
          />
        </div>
        <div className="stack-column">
          <Label htmlFor="password">{t("auth.register.passwordLabel")}</Label>
          <PasswordInput
            id="password"
            name="password"
            value={form.password}
            onChange={handleChange}
            required
          />
        </div>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? t("auth.register.submitting") : t("auth.register.submit")}
        </Button>
      </form>
    </section>
  );
};

export default RegisterPage;
